import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { CONFIG } from '@config';
import { FeatureFlagTypes } from '@generated/graphql';
import {
  type FileDocument,
  FileDocumentObjectTypes,
  FileDocumentTypes,
} from '@interfaces/fileDocument';
import { useCountrySpecificBackendIdDocumentTypes } from '@interfaces/IdVerification';
import type { User } from '@interfaces/user';
import { ComplianceStates } from '@interfaces/user';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { WorkflowCompletionContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionDialogContext';
import { FETCH_DOCUMENTS } from '@pages/client/components/documents.queries';
import { FETCH_IDS } from '@pages/client/components/iDs';
import { FETCH_INTEGRATIONS } from '@pages/devSettings/components/integrations.operations';
import {
  UserContext,
  useOrganizationFeatureFlag,
} from '@providers/userContextProvider';
import { type FileUploaderProps, fileUploader } from '@utils/fileUploader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSnackbar } from 'hephaestus/web';
import Persona, { type Client } from 'persona';
import type { InquiryError } from 'persona/dist/lib/interfaces';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdVerificationVisual } from './idVerification.visual';

dayjs.extend(utc);

const FETCH_USER = gql`
  query fetchUserIdVerification($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        physicalAddress {
          province
        }
        latestIdVerification {
          id
          documentExpiryDate
          documentID
          documentIssuingJurisdication
          documentType
          methodOfIDVerification
          verifiedDate
          userName
          address {
            city
            jurisdiction
            streetName
            postal
            unitNumber
            houseNumber
            neighborhood
            country
          }
          secondaryDocumentIssuingJurisdication
          secondaryDocumentType
          secondaryDocumentExpiryDate
          secondaryDocumentID
          secondaryDocumentUserName
          secondaryDocumentDateOfBirth
          secondaryDocumentVerifiedDate
          creditFileSource
        }
      }
    }
  }
`;

const FETCH_USER_FOR_PERSONA = gql`
  query fetchUserForPersona($userId: ObjectID!) {
    fetchUser(userId: $userId) {
      user {
        id
        physicalAddress {
          province
          jurisdiction
        }
        iDVerified
        iDCheckCompleted
        iDCheckRetrigger
        firstName
        middleName
        lastName
        dateOfBirth
        primaryEmail
        phone
        language
        organization {
          id
        }
      }
    }
  }
`;

const CREATE_ID = gql`
  mutation createUserIDVerificationInformation(
    $input: CreateUserIDVerificationInformationInput!
  ) {
    createUserIDVerificationInformation(input: $input) {
      idVerification {
        id
      }
    }
  }
`;

const UPDATE_ID = gql`
  mutation updateUserIDVerificationInformation(
    $input: UpdateUserIDVerificationInformationInput!
  ) {
    updateUserIDVerificationInformation(input: $input) {
      idVerification {
        id
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

const FETCH_FILE_DOCUMENTS = gql`
  query fetchFileDocumentsForIdVerification($idVerificationId: ObjectID!) {
    fetchFileDocuments(
      input: {
        filter: { sourceIds: [$idVerificationId], types: [ID_VERIFICATION] }
      }
    ) {
      fileDocuments {
        id
        fileName
        applicableDocumentId
      }
    }
  }
`;

const DELETE_FILE_DOCUMENT = gql`
  mutation deleteFileDocument($fileDocumentId: ObjectID!) {
    deleteFileDocument(input: { fileDocumentId: $fileDocumentId }) {
      fileDocument {
        id
      }
    }
  }
`;

const UPDATE_FILE_DOCUMENT = gql`
  mutation updateFileDocument($input: UpdateFileDocumentInput!) {
    updateFileDocument(input: $input) {
      fileDocument {
        id
      }
    }
  }
`;

export const IdVerification = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const graphqlClient = useApolloClient();
  const { i18n } = useTranslation();
  const { activeOrganization } = useContext(UserContext);
  const featureFlagDocumentIdVerification = useOrganizationFeatureFlag(
    FeatureFlagTypes.ConfigurableDocumentsIdVerification,
  );
  const [idData, setIdData] = useState<any>({
    idVerificationId: undefined,
    documentExpiryDate: null,
    documentID: null,
    documentIssuingJurisdication: null,
    documentType: null,
    methodOfIDVerification: null,
    verifiedDate: null,
    userName: null,
    address: {
      city: '',
      jurisdiction: '',
      streetName: '',
      postal: '',
      unitNumber: '',
      houseNumber: '',
      neighborhood: '',
      country: '',
    },
    secondaryDocumentIssuingJurisdication: undefined,
    secondaryDocumentType: undefined,
    secondaryDocumentExpiryDate: undefined,
    secondaryDocumentID: undefined,
    secondaryDocumentDateOfBirth: null,
    secondaryDocumentVerifiedDate: null,
    creditFileSource: undefined,
  });
  const { idDocumentTypes, loading: idDocTypesLoading } =
    useCountrySpecificBackendIdDocumentTypes();
  const [existingFileDocument1, setExistingFileDocument1] =
    useState<FileDocument>();
  const [file1, setFile1] = useState<File>();
  const [existingFileDocument2, setExistingFileDocument2] =
    useState<FileDocument>();
  const [file2, setFile2] = useState<File>();
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const { setHasQuebecAddress } = useContext(WorkflowCompletionContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [uploading, setUploading] = useState(false);

  const [updateFileDocument] = useMutation(UPDATE_FILE_DOCUMENT);

  const query = options.manualVerification
    ? FETCH_USER
    : FETCH_USER_FOR_PERSONA;
  const { data, loading } = useQuery(query, {
    variables: { userId },
    fetchPolicy: 'no-cache',
  });

  const [personaTemplateId, setPersonaTemplateId] = useState('');
  const { loading: integrationIsLoading } = useQuery(FETCH_INTEGRATIONS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          type: 'ID_VERIFICATION',
        },
      },
    },
    onCompleted: (response): void => {
      const personaConfig: any = response.fetchIntegrations.integrations.find(
        (integration: any) => integration.provider === 'PERSONA',
      );
      if (personaConfig) {
        setPersonaTemplateId(
          personaConfig.configuration.templateId ||
            CONFIG.services.personaTemplateId ||
            '',
        );
      } else {
        setPersonaTemplateId(CONFIG.services.personaTemplateId || '');
      }
    },
    onError: (): void => {
      setPersonaTemplateId(CONFIG.services.personaTemplateId || '');
    },
  });
  const [personaFields, setPersonaFields] = useState({
    nameFirst: '',
    nameMiddle: '',
    nameLast: '',
    birthdate: '',
    emailAddress: '',
    phoneNumber: '',
  });

  const { loading: fileDocumentsLoading } = useQuery(FETCH_FILE_DOCUMENTS, {
    skip: !idData?.idVerificationId,
    variables: {
      idVerificationId: idData.idVerificationId,
    },
    fetchPolicy: 'no-cache',
    onCompleted: (responseData) => {
      const isDualIdMethod = idData?.methodOfIDVerification === 'DUAL_METHOD';
      let firstDocument =
        responseData.fetchFileDocuments.fileDocuments?.[0] ?? undefined;
      let secondDocument =
        responseData.fetchFileDocuments.fileDocuments?.[1] ?? undefined;
      if (isDualIdMethod) {
        firstDocument = responseData.fetchFileDocuments.fileDocuments?.find(
          (document: { applicableDocumentId?: string }) =>
            document.applicableDocumentId === idData?.documentID,
        );
        secondDocument = responseData.fetchFileDocuments.fileDocuments?.find(
          (document: { applicableDocumentId?: string }) =>
            document.applicableDocumentId === idData?.secondaryDocumentID,
        );
        // cater for old uploaded documents that don't have an applicableDocumentId
        // They will perform without any change until the document is updated with an applicableDocumentId
        if (
          !firstDocument &&
          !responseData.fetchFileDocuments.fileDocuments?.[0]
            ?.applicableDocumentId
        ) {
          firstDocument = responseData.fetchFileDocuments.fileDocuments?.[0];
        }
        if (
          !secondDocument &&
          !responseData.fetchFileDocuments.fileDocuments?.[1]
            ?.applicableDocumentId
        ) {
          secondDocument = responseData.fetchFileDocuments.fileDocuments?.[1];
        }
      }
      setExistingFileDocument1(firstDocument);
      setExistingFileDocument2(secondDocument);
    },
  });

  const doDocumentUpload = async (
    verificationId: string,
    file: any,
    documentReferenceId?: string,
  ) => {
    const fileUploadProps: FileUploaderProps = {
      file,
      createFileInput: {
        objectType: FileDocumentObjectTypes.USER,
        objectId: userId,
        userId,
        fileName: file.name,
        type: FileDocumentTypes.ID_VERIFICATION,
        name: file.name,
        mediaType: file.type,
        permissionType: 'PUBLIC',
        sourceType: 'ID_DOCUMENT',
        sourceId: verificationId,
        applicableDocumentId: documentReferenceId,
      },
      onSuccess: () => {
        enqueueSnackbar(`ID document uploaded successfully: ${file.name}`, {
          variant: 'success',
        });
      },
      refetchQueries: [FETCH_DOCUMENTS, FETCH_IDS],
      apolloClient: graphqlClient,
    };

    await fileUploader(fileUploadProps);
  };

  const [doDocumentDelete] = useMutation(DELETE_FILE_DOCUMENT);

  const [createdId, { loading: createIdLoading }] = useMutation(CREATE_ID, {
    onCompleted: async (res: any) => {
      setUploading(true);
      const cratedId =
        res?.createUserIDVerificationInformation?.idVerification?.id || '';

      const primaryDocumentReferenceId = idData?.documentID;
      const secondaryDocumentReferenceId = idData?.secondaryDocumentID;

      if (file1)
        await doDocumentUpload(cratedId, file1, primaryDocumentReferenceId);
      if (file2)
        await doDocumentUpload(cratedId, file2, secondaryDocumentReferenceId);
      setHasQuebecAddress(false);
      await onNext();
      setUploading(false);
    },
    variables: {
      input: {
        ...idData,
        verifiedDate: idData.verifiedDate || dayjs().utc().format('YYYY-MM-DD'),
        ...(idData.methodOfIDVerification === 'DUAL_METHOD' && {
          secondaryDocumentVerifiedDate:
            idData.secondaryDocumentVerifiedDate ||
            dayjs().utc().format('YYYY-MM-DD'),
        }),
        address: Object.values(idData.address || {}).every((value) => !value)
          ? undefined
          : idData.address,
        userId,
      },
    },
  });

  const [updateId, { loading: updateIdLoading }] = useMutation(UPDATE_ID, {
    onCompleted: async () => {
      setUploading(true);
      if (existingFileDocument1?.id) {
        await updateFileDocument({
          variables: {
            input: {
              fileDocumentId: existingFileDocument1.id,
              applicableDocumentId: idData?.documentID,
            },
          },
        });
      }
      if (existingFileDocument2?.id) {
        await updateFileDocument({
          variables: {
            input: {
              fileDocumentId: existingFileDocument2.id,
              applicableDocumentId: idData?.secondaryDocumentID,
            },
          },
        });
      }
      if (file1 && file1.lastModified > 0)
        await doDocumentUpload(
          idData.idVerificationId,
          file1,
          idData?.documentID,
        );
      if (file2 && file2.lastModified > 0)
        await doDocumentUpload(
          idData.idVerificationId,
          file2,
          idData?.secondaryDocumentID,
        );
      await Promise.all(
        filesToDelete.map((id) =>
          doDocumentDelete({ variables: { fileDocumentId: id } }),
        ),
      );
      setHasQuebecAddress(false);
      await onNext();
      setUploading(false);
    },
    variables: {
      input: {
        ...idData,
        address: Object.values(idData.address || {}).every((value) => !value)
          ? undefined
          : idData.address,
      },
    },
  });

  const replaceFile1 = (newFile?: File) => {
    setFile1(newFile);

    if (existingFileDocument1?.id) {
      setFilesToDelete([...filesToDelete, existingFileDocument1.id]);
      setExistingFileDocument1(undefined);
    }
  };

  const replaceFile2 = (newFile?: File) => {
    setFile2(newFile);

    if (existingFileDocument2?.id) {
      setFilesToDelete([...filesToDelete, existingFileDocument2.id]);
      setExistingFileDocument2(undefined);
    }
  };

  const [updateUser] = useMutation(UPDATE_USER);

  const continueFunc = async () => {
    setWorkflowData({ ...workflowData, currentUserId: userId });
    if (idData.idVerificationId) {
      await updateId();
    } else {
      await createdId();
    }
  };

  useEffect(() => {
    if (data) {
      const userData: User = data.fetchUser.user;
      setHasQuebecAddress(
        userData.physicalAddress?.province === 'QC' ||
          userData.physicalAddress?.jurisdiction === 'CA_QC',
      );
      if (userData.latestIdVerification && options.manualVerification) {
        setIdData({
          idVerificationId: userData.latestIdVerification?.id || undefined,
          documentExpiryDate:
            userData.latestIdVerification?.documentExpiryDate || undefined,
          documentID: userData.latestIdVerification?.documentID || undefined,
          documentIssuingJurisdication:
            userData.latestIdVerification?.documentIssuingJurisdication ||
            undefined,
          documentType:
            userData.latestIdVerification?.documentType || undefined,
          methodOfIDVerification:
            userData.latestIdVerification?.methodOfIDVerification || undefined,
          verifiedDate:
            userData.latestIdVerification?.verifiedDate || undefined,
          userName: userData.latestIdVerification?.userName || undefined,
          address: userData.latestIdVerification?.address
            ? {
                city: userData.latestIdVerification?.address?.city || undefined,
                jurisdiction:
                  userData.latestIdVerification?.address?.jurisdiction ||
                  undefined,
                streetName:
                  userData.latestIdVerification?.address?.streetName ||
                  undefined,
                postal:
                  userData.latestIdVerification?.address?.postal || undefined,
                unitNumber:
                  userData.latestIdVerification?.address?.unitNumber ||
                  undefined,
                houseNumber:
                  userData.latestIdVerification?.address?.houseNumber ||
                  undefined,
                neighborhood:
                  userData.latestIdVerification?.address?.neighborhood ||
                  undefined,
                country:
                  userData.latestIdVerification?.address?.country || undefined,
              }
            : {},
          secondaryDocumentIssuingJurisdication:
            userData.latestIdVerification
              ?.secondaryDocumentIssuingJurisdication || undefined,
          secondaryDocumentType:
            userData.latestIdVerification?.secondaryDocumentType || undefined,
          secondaryDocumentExpiryDate:
            userData.latestIdVerification?.secondaryDocumentExpiryDate ||
            undefined,
          secondaryDocumentID:
            userData.latestIdVerification?.secondaryDocumentID || undefined,
          creditFileSource:
            userData.latestIdVerification?.creditFileSource || undefined,
          secondaryDocumentUserName:
            userData.latestIdVerification?.secondaryDocumentUserName ||
            undefined,
          secondaryDocumentDateOfBirth:
            userData.latestIdVerification?.secondaryDocumentDateOfBirth ||
            undefined,
          secondaryDocumentVerifiedDate:
            userData.latestIdVerification?.secondaryDocumentVerifiedDate ||
            undefined,
        });
      } else if (userData && options.automatedVerification) {
        setPersonaFields({
          nameFirst: userData.firstName ?? '',
          nameMiddle: userData.middleName ?? '',
          nameLast: userData.lastName ?? '',
          birthdate: userData.dateOfBirth
            ? dayjs(userData.dateOfBirth).utc().format('YYYY-MM-DD')
            : '',
          emailAddress: userData.primaryEmail ?? '',
          phoneNumber: userData.phone ?? '',
        });
        const iDCheckCompleted = userData.iDCheckCompleted ?? false;
        const iDVerified = userData.iDVerified ?? false;
        const iDCheckRetrigger = userData.iDCheckRetrigger ?? false;
        if (iDCheckCompleted && iDVerified && !iDCheckRetrigger) {
          setHasQuebecAddress(false);
          // skip Persona flow
          onNext();
        }
      }
    }
  }, [data]);

  const getPersonaInquiryStorageKey = () => `persona_inquiry_${userId}`;

  const openPersona = () =>
    new Promise<void>((resolve) => {
      const storageKey = getPersonaInquiryStorageKey();
      const existingInquiryId = localStorage.getItem(storageKey);

      const client: Client = new Persona.Client({
        // Only use templateId and fields when starting a new inquiry
        ...(existingInquiryId
          ? { inquiryId: existingInquiryId }
          : {
              templateId: personaTemplateId,
              referenceId: userId,
              fields: personaFields,
            }),
        environment: CONFIG.app.env === 'production' ? 'production' : 'sandbox',
        language: i18n.language,
        onReady: () => client.open(),
        onEvent: (name: string, metadata?: { [key: string]: unknown }) => {
          // Store the inquiry ID when the inquiry starts so user can resume if they leave
          if (name === 'start' && metadata?.inquiryId) {
            localStorage.setItem(storageKey, metadata.inquiryId as string);
          }
        },
        onCancel: () => resolve(),
        onComplete: async ({
          inquiryId,
          status,
        }: {
          inquiryId: string;
          status: string;
        }) => {
          // Clear stored inquiry on completion
          localStorage.removeItem(storageKey);
          await updateUser({
            variables: {
              input: {
                userId,
                iDCheckCompleted: true,
                iDCheckRetrigger: false,
                ...(!['completed', 'pending'].includes(status)
                  ? {
                      iDCheckRetrigger: true,
                      complianceState: ComplianceStates.NEEDS_REVIEW,
                      complianceIssueSource: `Persona was not initialized on their device -- Persona sdk logs: ${inquiryId}`,
                    }
                  : {}),
              },
            },
          });
          setHasQuebecAddress(false);
          await onNext();
          resolve();
        },
        onError: async (error: InquiryError) => {
          // Clear stored inquiry on error
          localStorage.removeItem(storageKey);
          await updateUser({
            variables: {
              input: {
                userId,
                iDCheckCompleted: true,
                iDCheckRetrigger: true,
                complianceState: ComplianceStates.NEEDS_REVIEW,
                complianceIssueSource: `Persona was not initialized on their device -- Persona sdk logs: ${error.code}`,
              },
            },
          });
          setHasQuebecAddress(false);
          await onNext();
          resolve();
        },
      });
    });

  return (
    <IdVerificationVisual
      options={options}
      idDocumentTypes={idDocumentTypes}
      idData={idData}
      updateId={setIdData}
      continueFunc={continueFunc}
      openPersona={openPersona}
      existingFileDocument1={existingFileDocument1}
      file1={file1}
      setFile1={replaceFile1}
      existingFileDocument2={existingFileDocument2}
      file2={file2}
      setFile2={replaceFile2}
      loading={
        loading ||
        stepLoading ||
        integrationIsLoading ||
        idDocTypesLoading ||
        fileDocumentsLoading
      }
      updating={createIdLoading || updateIdLoading || uploading}
      workflowCompletion={workflowCompletion}
      featureFlagDocumentIdVerification={featureFlagDocumentIdVerification}
    />
  );
};

export default IdVerification;
